import React from 'react';
import { Link } from 'react-router-dom';
import {
  WEBSITE_STATS,
  FRAGMENTATION_TOOLS,
  DEPGUARD_PRODUCT,
  VIGIL_PRODUCT,
  PRISM_PRODUCT,
} from './websiteData';
import {
  DepGuardDashboardIllustration,
  CrossProjectNetworkIllustration,
} from './WebsiteIllustrations';
import { WEBSITE_TESTIMONIALS } from './websiteData';
import './WebsiteHomePage.css';

/* ── Hero ──────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="dgw-hero">
      <div className="dgw-hero-inner dgw-hero-split">
        {/* Left column: text + CTAs */}
        <div className="dgw-hero-left">
          <div className="dgw-badge">
            <span className="dgw-badge-dot" />
            Code analysis, SBOM security, and compliance
          </div>

          <h1>One graph.<br/><em>Every security question answered.</em></h1>

          <p className="dgw-hero-lead">
            PRISM parses your code into a Code Property Graph. DepGuard and Vigil query it
            for vulnerabilities, licenses, compliance, and SBOM analysis. One parse. Every answer.
          </p>

          <div className="dgw-product-pills">
            <Link to="/website/product/depguard" className="dgw-product-pill depguard">🛡️ DepGuard</Link>
            <Link to="/website/product/vigil"    className="dgw-product-pill vigil">👁️ Vigil</Link>
            <Link to="/website/product/prism"    className="dgw-product-pill prism">◈ Prism</Link>
          </div>

          <div className="dgw-hero-actions">
            <Link to="/trial?plan=professional" className="dgw-btn dgw-btn-primary dgw-btn-lg">
              Start with DepGuard →
            </Link>
            <Link to="/website/product/prism" className="dgw-btn dgw-btn-ghost dgw-btn-lg">
              Explore the platform
            </Link>
          </div>

          {/* Mini terminal */}
          <div className="dgw-terminal" style={{maxWidth:'unset', margin:0}}>
            <div className="dgw-terminal-bar">
              <span className="dgw-terminal-dot" />
              <span className="dgw-terminal-dot" />
              <span className="dgw-terminal-dot" />
              <span className="dgw-terminal-title">depguard scan — zsh</span>
            </div>
            <div className="dgw-terminal-body">
              <div><span className="t-prompt">❯ </span><span className="t-cmd">prism sbom ./src --format cyclonedx -o sbom.cdx.json</span></div>
              <div><span className="t-prism">◈ PRISM </span><span className="t-muted">CPG built in </span><span className="t-ok">38ms</span><span className="t-muted"> · 7 languages</span></div>
              <div><span className="t-muted">  📦 SBOM  </span><span className="t-ok">143 packages · CycloneDX 1.5</span></div>
              <div><span className="t-vigil">👁 Vigil  </span><span className="t-muted">140 rules loaded... </span><span className="t-ok">complete</span></div>
              <div><span className="t-err">  ✗ CRITICAL  CVE-2024-21626  </span><span className="t-warn">reachable</span></div>
              <div><span className="t-ok">  ✓ EU CRA   93% compliant · Policy gate: PASSED</span></div>
            </div>
          </div>
        </div>

        {/* Right column: live dashboard illustration */}
        <div className="dgw-hero-right">
          <div className="dgw-hero-right-glow" />
          <div className="dgw-hero-illus-wrap">
            <DepGuardDashboardIllustration />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Stats ─────────────────────────────────────────────────── */
function StatsRow() {
  return (
    <div className="dgw-stats-row">
      {WEBSITE_STATS.map(s => (
        <div key={s.label} className="dgw-stat-item">
          <span className={`dgw-stat-value ${s.colorClass}`}>{s.value}</span>
          <span className="dgw-stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Cross-Project Impact Section ──────────────────────────── */
function ImpactSection() {
  return (
    <section className="dgw-impact-section">
      <div className="dgw-impact-inner">
        <div className="dgw-impact-text">
          <span className="dgw-section-tag" style={{background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)', display:'inline-block', marginBottom:16}}>
            Blast radius
          </span>
          <h2>One vulnerable package.<br/>Dozens of affected projects.</h2>
          <p>
            When log4j hit in December 2021, security teams at large enterprises spent weeks
            answering one question: which services are actually affected? DepGuard's
            Cross-Project Impact Graph answers it in seconds — for every CVE, every bad
            license, every abandoned package across your entire portfolio.
          </p>
          <ul className="dgw-impact-points">
            <li>Instantly see which projects use a vulnerable dependency</li>
            <li>Filter by CVE severity, license risk, or package health score</li>
            <li>Prioritize remediation — most-exposed projects first</li>
            <li>Export affected-project list for incident response</li>
          </ul>
          <Link to="/website/product/depguard" className="dgw-btn dgw-btn-primary" style={{display:'inline-flex'}}>
            See the Impact Graph →
          </Link>
        </div>
        <div className="dgw-impact-graph-wrap">
          <CrossProjectNetworkIllustration />
        </div>
      </div>
    </section>
  );
}

/* ── Problem ───────────────────────────────────────────────── */
function ProblemSection() {
  return (
    <section className="dgw-problem-section">
      <div style={{maxWidth:'1100px', margin:'0 auto'}}>
        <div style={{textAlign:'center', marginBottom:16}}>
          <span className="dgw-section-tag" style={{background:'rgba(99,102,241,.08)', color:'#6366f1', border:'1px solid rgba(99,102,241,.2)'}}>The problem</span>
        </div>
        <h2 style={{fontSize:'clamp(28px,4vw,48px)', fontWeight:800, letterSpacing:'-1px', textAlign:'center', marginBottom:16, color:'#0f172a'}}>Four tools. Four parses. Same codebase.</h2>
        <p style={{textAlign:'center', fontSize:18, color:'#475569', marginBottom:56, maxWidth:600, margin:'0 auto 56px'}}>
          Most teams run a SAST scanner, an SCA scanner, a license checker, and a compliance tool.
          Each one parses the same source. Each one produces its own dashboard. None of them talk to each other.
        </p>

        <div className="dgw-fragmentation-grid">
          <div>
            <div className="dgw-frag-tools">
              {FRAGMENTATION_TOOLS.map(t => (
                <div key={t.name} className="dgw-frag-tool">
                  <div className="dgw-frag-tool-icon">{t.icon}</div>
                  <div className="dgw-frag-tool-name">{t.name}</div>
                  <div className="dgw-frag-tool-pain">{t.pain}</div>
                </div>
              ))}
            </div>
            <div style={{textAlign:'center', marginTop:20, fontSize:14, color:'#64748b', fontWeight:600}}>
              ≈ 4× the cost. 4× the maintenance. 4× the alerts.
            </div>
          </div>

          <div style={{display:'flex', flexDirection:'column', justifyContent:'center', gap:24}}>
            <div style={{textAlign:'center', fontSize:32, color:'#6366f1'}}>→</div>
            <div className="dgw-frag-solution">
              <h3>Parse once. Know everything.</h3>
              <p>
                PRISM builds the Code Property Graph from your source. Vigil queries it
                for code vulnerabilities. DepGuard queries it for supply chain risk.
                Same graph, same data, no duplicated infrastructure.
              </p>
              <ul>
                <li>One CPG for SAST, SCA, license checks, and compliance</li>
                <li>SBOM-native correlation links code findings to package-level risk</li>
                <li>EU CRA ready by design. Article 20 evidence generates on demand.</li>
                <li>Single Go binary. Ships at ~12MB with zero external dependencies.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Product Cards ─────────────────────────────────────────── */
function ProductCard({ product }) {
  const { id, name, icon, tagline, oneLiner, features, accentColor } = product;
  const to = `/website/product/${id}`;
  const displayFeatures = features.slice(0, 4);
  return (
    <article className={`dgw-product-card ${id}`}>
      <div className="dgw-card-icon">{icon}</div>
      <span className="dgw-card-kicker">{tagline}</span>
      <h3>{name}</h3>
      <p className="dgw-tagline" style={{color:'#64748b', fontSize:14, marginBottom:20}}>{oneLiner}</p>
      <ul className="dgw-feature-list">
        {displayFeatures.map(f => (
          <li key={f.title}><strong style={{color:'#1e293b', marginRight:4}}>{f.title}.</strong> {f.desc}</li>
        ))}
      </ul>
      <Link to={to} className="dgw-btn dgw-btn-ghost" style={{marginTop:'auto', borderColor: accentColor + '44', color: accentColor}}>
        Learn more →
      </Link>
    </article>
  );
}

function ProductsSection() {
  return (
    <section className="dgw-section">
      <div className="dgw-section-header">
        <span className="dgw-section-tag">The platform</span>
        <h2>Three products. One parse.</h2>
        <p className="dgw-section-lead">
          Build the graph once. Each product gets the same structural understanding of your
          code — no separate parse steps, no format translation, no duplicated work.
        </p>
      </div>
      <div className="dgw-product-cards">
        <ProductCard product={DEPGUARD_PRODUCT} />
        <ProductCard product={VIGIL_PRODUCT} />
        <ProductCard product={PRISM_PRODUCT} />
      </div>
    </section>
  );
}

/* ── Platform Diagram ──────────────────────────────────────── */
function PlatformSection() {
  return (
    <section className="dgw-platform-section">
      <div className="dgw-platform-inner">
        <div className="dgw-section-header">
          <span className="dgw-section-tag">Architecture</span>
          <h2>How it actually works.</h2>
          <p className="dgw-section-lead">
            PRISM parses your source code into a Code Property Graph. Vigil and DepGuard
            run as query engines against it. You don't maintain three pipelines.
            One parse step serves everything.
          </p>
        </div>

        <div className="dgw-platform-diagram">
          {/* Clients */}
          <div className="dgw-platform-clients">
            <div className="dgw-platform-node" style={{borderColor:'rgba(99,102,241,.3)'}}>
              <div className="dgw-platform-node-icon">🛡️</div>
              <div className="dgw-platform-node-name" style={{color:'#6366f1'}}>DepGuard</div>
              <div className="dgw-platform-node-desc">Queries CPG for<br/>supply chain risk</div>
            </div>
            <div className="dgw-platform-node" style={{borderColor:'rgba(139,92,246,.3)'}}>
              <div className="dgw-platform-node-icon">👁️</div>
              <div className="dgw-platform-node-name" style={{color:'#8b5cf6'}}>Vigil</div>
              <div className="dgw-platform-node-desc">Queries CPG for<br/>vulnerabilities</div>
            </div>
          </div>

          {/* Connectors */}
          <div className="dgw-platform-connector">
            <div className="dgw-platform-connector-line" />
            <span>queries</span>
            <div className="dgw-platform-connector-line" />
          </div>

          {/* Foundation */}
          <div className="dgw-platform-node foundation">
            <div className="dgw-platform-node-icon">◈</div>
            <div className="dgw-platform-node-name">PRISM</div>
            <div className="dgw-platform-node-desc">
              Code Property Graph Engine<br/>
              AST · CFG · DDG · CDG · Call Graph<br/>
              7 languages · 38ms cold start
            </div>
          </div>

          {/* Source */}
          <div className="dgw-platform-connector">
            <div className="dgw-platform-connector-line" />
            <span>parses</span>
            <div className="dgw-platform-connector-line" />
          </div>

          <div className="dgw-platform-node" style={{minWidth:180}}>
            <div className="dgw-platform-node-icon">📂</div>
            <div className="dgw-platform-node-name" style={{color:'#64748b'}}>Your Code</div>
            <div className="dgw-platform-node-desc">Source + SBOM<br/>7 languages</div>
          </div>
        </div>

        <div style={{textAlign:'center', marginTop:48}}>
          <Link to="/website/product/prism" className="dgw-btn dgw-btn-ghost" style={{borderColor:'rgba(6,182,212,.4)', color:'#0891b2'}}>
            Deep-dive into PRISM →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ── Testimonials ──────────────────────────────────────────── */
function TestimonialsSection() {
  return (
    <section className="dgw-testimonials-section">
      <div style={{maxWidth:1100, margin:'0 auto'}}>
        <div className="dgw-section-header">
          <span className="dgw-section-tag">What teams say</span>
          <h2>From the teams using it.</h2>
          <p className="dgw-section-lead">
            Early adopters from fintech, SaaS, and regulated enterprise — sharing what changed
            when they stopped stitching together four separate tools.
          </p>
        </div>
        <div className="dgw-testimonials-grid">
          {WEBSITE_TESTIMONIALS.map(t => (
            <div key={t.name} className="dgw-testimonial-card">
              <div className="dgw-testimonial-stars">★★★★★</div>
              <p className="dgw-testimonial-quote">"{t.quote}"</p>
              <div className="dgw-testimonial-author">
                <div className="dgw-testimonial-avatar">
                  {t.name.split(' ').map(w => w[0]).join('').slice(0,2)}
                </div>
                <div>
                  <div className="dgw-testimonial-name">{t.name}</div>
                  <div className="dgw-testimonial-company">{t.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── CTA Band ──────────────────────────────────────────────── */
function CtaBand() {
  return (
    <section className="dgw-cta-band">
      <div className="dgw-cta-band-inner">
        <h2>Try it with your own codebase.</h2>
        <p>
          Upload an SBOM or point PRISM at a repository. See results in under 60 seconds.
        </p>
        <div className="dgw-cta-band-actions">
          <Link to="/trial?plan=professional&product=depguard" className="dgw-btn-cta-white">
            🛡️ Start with DepGuard
          </Link>
          <Link to="/website/product/vigil" className="dgw-btn-cta-ghost">
            👁️ Explore Vigil
          </Link>
          <Link to="/website/product/prism" className="dgw-btn-cta-ghost">
            ◈ See PRISM
          </Link>
          <Link to="/website/product/prism" className="dgw-btn-cta-ghost">
            📖 Prism Book
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ── Page ──────────────────────────────────────────────────── */
export default function WebsiteHomePage() {
  return (
    <div className="dgw-page-wrap">
      <Hero />
      <StatsRow />
      <ImpactSection />
      <ProblemSection />
      <ProductsSection />
      <TestimonialsSection />
      <PlatformSection />
      <CtaBand />
    </div>
  );
}
